import type { ActiveRoute } from "@/__main__/router.mjs";
import {
  componentCache,
  getModulePath,
  matchRoutes,
} from "@/__main__/router.mjs";
import { catchAllRoutes } from "@/routes/constants.mjs";
import { devError } from "@/util/dev.mjs";
import dynamicImport from "@/util/dynamic-import.mjs";
import globals from "@/util/global-whitelist.mjs";
import {
  APP_ROUTE_TYPE,
  APP_ROUTE_VERSION,
  IS_APP_ROUTE,
} from "@/util/mod-pathname.mjs";

const obs =
  typeof MutationObserver !== "undefined"
    ? new MutationObserver((mutations) => {
        for (const { addedNodes, type, attributeName, target } of mutations) {
          for (const node of addedNodes) {
            if (!(node instanceof Element)) continue;
            if (node.nodeType !== Node.ELEMENT_NODE) continue;
            const links = Array.from(node.querySelectorAll("[href]"));
            if (node.getAttribute("href")) links.push(node);
            for (const link of links) {
              processLink(link);
            }
          }
          if (type === "attributes" && attributeName === "href") {
            processLink(target);
          }
        }
      })
    : null;

function findLink(element) {
  if (element.tagName === "A") return element;
  const parent = element.parentNode;
  if (!parent) return null;
  const link = findLink(parent);
  return link;
}

async function preloadAnchor({ target }) {
  const link = findLink(target);
  if (!link || !link.href) return;
  try {
    const url = new URL(link.href);
    if (url.host !== globals.location.host) return;
    await preloadPath(url.pathname);
  } catch {
    devError("Failed to preload anchor", link);
  }
}

export async function preloadPath(pathname) {
  const matches = matchRoutes(stripQueryString(stripAppRoute(pathname)));
  const route =
    matches.find(({ component, redirect }) => {
      return component || redirect;
    }) ?? ({} as ActiveRoute);
  let { component } = route;
  const { redirect } = route;
  if (typeof redirect === "string") {
    const matches = matchRoutes(stripQueryString(stripAppRoute(redirect)));
    ({ component } =
      matches.find(({ component }) => {
        return component;
      }) ?? {});
  }
  if (
    !component ||
    componentCache.get(component) ||
    typeof component !== "string"
  )
    return;
  const modulePath = getModulePath(component);
  componentCache.set(component, await dynamicImport(modulePath));
}

export function observe(root = globals.document?.body) {
  obs?.observe(root, {
    attributeFilter: ["href"],
    childList: true,
    subtree: true,
  });
  globals.addEventListener("pointerover", preloadAnchor);
  globals.document.addEventListener("focusin", preloadAnchor);
}

export function unobserve() {
  obs?.disconnect();
  globals.removeEventListener("pointerover", preloadAnchor);
  globals.removeEventListener("focusin", preloadAnchor);
}

function processLink(link) {
  if (!isLinkValid(link)) return;

  const href = link.getAttribute("href");

  // rename the link if its relevant for renaming
  if (
    IS_APP_ROUTE &&
    href.startsWith("/") &&
    !/^\/(app\/|v(?=\d))/.test(href)
  ) {
    link.setAttribute("href", `/${APP_ROUTE_TYPE}${APP_ROUTE_VERSION}${href}`);
  }
}

function isLinkValid(link) {
  const href = link.getAttribute("href");
  if (!href) {
    devError(
      "An <a> element on the page does not have href attribute (invalid).",
      link,
    );
    return false;
  }

  // check, if the link is known to the router to find invalid links
  if (href.startsWith("/")) {
    const matches = matchRoutes(stripQueryString(stripAppRoute(href)));
    const validMatch = matches.find((match) => !match[catchAllRoutes]);

    if (!validMatch && !link.dataset?.exempt) {
      devError("An <a> element on the page does not have a matching route.", {
        href,
        matches,
        link,
      });
      link.style.animation = "0.5s ease-in infinite alternate invalid-link";
      link.style.textDecoration = "line-through";
      return false;
    }
  } else if (href.startsWith(globals.location.origin)) {
    devError(
      `A link can not start with the origin (${globals.location.origin}). Please use a relative path.`,
      href,
    );
    return false;
  }

  return true;
}

function stripAppRoute(path) {
  return path.replace(/^\/(?:app\/|v(?=\d))(.*?)\//, "/");
}

function stripQueryString(path) {
  return path.replace(/\?.*/, "");
}
